import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import 'ol/ol.css';
import { Map, View } from 'ol';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import XYZ from 'ol/source/XYZ';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { fromLonLat } from 'ol/proj';
import { Style, Icon, Text, Fill, Stroke } from 'ol/style';
import { defaults as defaultInteractions } from 'ol/interaction';
import MouseWheelZoom from 'ol/interaction/MouseWheelZoom';

const FacilityMap = forwardRef(({ facilities = [], onFacilityClick }, ref) => {
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const [currentLayer, setCurrentLayer] = useState('street'); // 'street' or 'satellite'
    const streetLayerRef = useRef(null);
    const satelliteLayerRef = useRef(null);
    const vectorLayerRef = useRef(null);

    useEffect(() => {
        if (!mapRef.current) {
            console.log('mapRef.current is null');
            return;
        }

        console.log('Initializing map with facilities:', facilities);

        // Create vector data source
        const vectorSource = new VectorSource();

        // Create feature points for each facility
        facilities.forEach(facility => {
            // 检查设施是否有有效的坐标
            if (!facility.latitude || !facility.longitude) {
                console.warn(`Facility ${facility.name} has invalid coordinates`);
                return;
            }

            const feature = new Feature({
                geometry: new Point(fromLonLat([facility.longitude, facility.latitude])),
                facilityData: facility // Store facility data in feature
            });

            // 使用 facility 的颜色属性，如果没有则使用默认颜色
            const facilityColor = facility.color || '#13c2c2';

            const getCategoryIcon = (category) => {
                switch (category?.toLowerCase()) {
                    case 'hospital': return '🏥';
                    case 'office': return '🏢';
                    case 'manufacturing': return '🏭';
                    case 'warehouse': return '📦';
                    case 'retail': return '🛍️';
                    default: return '🏗️';
                }
            };

            // Create icon style with facility color
            const iconSvg = `
                <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">
                    <circle cx="18" cy="18" r="14" fill="${facilityColor}" stroke="white" stroke-width="2"/>
                    <text x="18" y="22" text-anchor="middle" font-size="12" fill="white">${getCategoryIcon(facility.category)}</text>
                </svg>`;

            feature.setStyle(new Style({
                image: new Icon({
                    src: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(iconSvg),
                    scale: 1
                }),
                text: new Text({
                    text: facility.name,
                    offsetY: -25,
                    fill: new Fill({ color: '#333' }),
                    stroke: new Stroke({ color: '#fff', width: 2 }),
                    font: '12px Arial',
                    textAlign: 'center'
                })
            }));

            vectorSource.addFeature(feature);
        });

        // Create vector layer
        const vectorLayer = new VectorLayer({
            source: vectorSource
        });
        vectorLayerRef.current = vectorLayer;

        // Create street layer
        const streetLayer = new TileLayer({
            source: new OSM(),
            visible: true
        });
        streetLayerRef.current = streetLayer;

        // Create satellite layer (using Google satellite imagery)
        const satelliteLayer = new TileLayer({
            source: new XYZ({
                url: 'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
                crossOrigin: 'anonymous'
            }),
            visible: false
        });
        satelliteLayerRef.current = satelliteLayer;

        // Calculate center point based on facilities
        let centerCoordinates = [0, 0]; // Default to [0, 0]
        let zoomLevel = 2;
        
        if (facilities.length > 0) {
            const validFacilities = facilities.filter(f => f.latitude && f.longitude);
            
            if (validFacilities.length > 0) {
                // Calculate average coordinates
                const avgLat = validFacilities.reduce((sum, f) => sum + f.latitude, 0) / validFacilities.length;
                const avgLng = validFacilities.reduce((sum, f) => sum + f.longitude, 0) / validFacilities.length;
                centerCoordinates = [avgLng, avgLat];
                
                // Adjust zoom level based on spread of facilities
                const latSpread = Math.abs(Math.max(...validFacilities.map(f => f.latitude)) - Math.min(...validFacilities.map(f => f.latitude)));
                const lngSpread = Math.abs(Math.max(...validFacilities.map(f => f.longitude)) - Math.min(...validFacilities.map(f => f.longitude)));
                const maxSpread = Math.max(latSpread, lngSpread);
                
                if (maxSpread < 0.01) zoomLevel = 15;
                else if (maxSpread < 0.1) zoomLevel = 12;
                else if (maxSpread < 1) zoomLevel = 9;
                else if (maxSpread < 10) zoomLevel = 6;
                else zoomLevel = 4;
            }
        }

        // Create map with custom interactions (excluding mouse wheel zoom)
        const map = new Map({
            target: mapRef.current,
            layers: [
                streetLayer,
                satelliteLayer,
                vectorLayer
            ],
            view: new View({
                center: fromLonLat(centerCoordinates),
                zoom: zoomLevel
            }),
            // 使用默认交互但排除鼠标滚轮缩放
            interactions: defaultInteractions({
                mouseWheelZoom: false // 禁用鼠标滚轮缩放
            })
        });

        // Add click event listener
        map.on('click', (event) => {
            const feature = map.forEachFeatureAtPixel(event.pixel, (feature) => {
                return feature;
            });

            if (feature) {
                const facilityData = feature.get('facilityData');
                if (facilityData) {
                    console.log('Clicked facility data:', facilityData);
                    console.log('Facility details:', {
                        ID: facilityData.id,
                        Name: facilityData.name,
                        Category: facilityData.category,
                        Company: facilityData.company_name,
                        Area: facilityData.area,
                        Address: facilityData.address,
                        Year: facilityData.year,
                        EUI: facilityData.eui,
                        Coordinates: [facilityData.longitude, facilityData.latitude],
                        Description: facilityData.description
                    });
                    
                    // 通知父组件设施被点击
                    if (onFacilityClick && typeof onFacilityClick === 'function') {
                        onFacilityClick(facilityData);
                    }
                }
            }
        });

        // Add mouse hover effect
        map.on('pointermove', (event) => {
            const feature = map.forEachFeatureAtPixel(event.pixel, (feature) => {
                return feature;
            });

            map.getTargetElement().style.cursor = feature ? 'pointer' : '';
            
            // Optional: Show tooltip on hover
            if (feature) {
                const facilityData = feature.get('facilityData');
                if (facilityData) {
                    map.getTargetElement().title = `${facilityData.name} (${facilityData.category})`;
                }
            } else {
                map.getTargetElement().title = '';
            }
        });

        mapInstance.current = map;
        
        console.log('Map created successfully with', facilities.length, 'facilities');

        // Cleanup function
        return () => {
            if (mapInstance.current) {
                mapInstance.current.setTarget(null);
            }
        };
    }, [facilities]); // Add facilities as dependency

    // Layer switching function
    const switchLayer = (layerType) => {
        if (layerType === 'street') {
            if (streetLayerRef.current) {
                streetLayerRef.current.setVisible(true);
            }
            if (satelliteLayerRef.current) {
                satelliteLayerRef.current.setVisible(false);
            }
            setCurrentLayer('street');
        } else if (layerType === 'satellite') {
            if (streetLayerRef.current) {
                streetLayerRef.current.setVisible(false);
            }
            if (satelliteLayerRef.current) {
                satelliteLayerRef.current.setVisible(true);
            }
            setCurrentLayer('satellite');
        }
    };

    // Function to focus on a specific facility (can be called from parent component)
    const focusOnFacility = (facilityId) => {
        const facility = facilities.find(f => f.id === facilityId);
        if (facility && facility.latitude && facility.longitude && mapInstance.current) {
            const view = mapInstance.current.getView();
            view.animate({
                center: fromLonLat([facility.longitude, facility.latitude]),
                zoom: 15,
                duration: 1000
            });
        }
    };

    // Make focusOnFacility available to parent component
    useImperativeHandle(ref, () => ({
        focusOnFacility
    }), [facilities]);

    return (
        <div style={{ 
            width: '100%', 
            height: '100%',
            position: 'relative',
            border: '1px solid #d9d9d9',
            borderRadius: '8px'
        }}>
            {/* Layer switching buttons */}
            <div style={{ 
                position: 'absolute', 
                bottom: '2px', 
                left: '2px', 
                zIndex: 1000,
                display: 'flex',
                gap: '2px'
            }}>
                <button
                    onClick={() => switchLayer('street')}
                    style={{
                        padding: '4px 8px',
                        backgroundColor: '#ffffff75',
                        opacity: 0.8,
                        color: currentLayer === 'street' ? '#1890ff' : '#333',
                        border: '1px solid #d9d9d9',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '9px',
                        fontWeight: '400',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    }}
                >
                    Street
                </button>
                <button
                    onClick={() => switchLayer('satellite')}
                    style={{
                        padding: '4px 8px',
                        backgroundColor: '#ffffff75',
                        opacity: 0.8,
                        color: currentLayer === 'satellite' ? '#1890ff' : '#333',
                        border: '1px solid #d9d9d9',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '9px',
                        fontWeight: '400',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    }}
                >
                    Satellite
                </button>
            </div>


            
            {/* Map container */}
            <div 
                ref={mapRef} 
                style={{ 
                    width: '100%', 
                    height: '100%'
                }}
            />
        </div>
    );
});

export default FacilityMap;